import { rootApi } from "../../../lib/api/rootApi";
import type {
  ClassDistributionTargetsResponse,
  ClearStudentTargetArgs,
  SetStudentTargetArgs,
} from "../types/distribution";

export const classDistributionApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getClassDistributionTargets: builder.query<
      ClassDistributionTargetsResponse,
      { classId: string }
    >({
      queryFn: async ({ classId }, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_distribution_get_class_targets",
          params: { p_class_id: classId },
        });
        if (error) return { error };
        return { data: data as ClassDistributionTargetsResponse };
      },
      providesTags: (_result, _error, { classId }) => [
        { type: "ClassDistribution", id: classId },
      ],
    }),

    setStudentDistributionTarget: builder.mutation<
      { id: string },
      SetStudentTargetArgs
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_distribution_set_student_target",
          params: {
            p_student_id: args.studentId,
            p_target_class_id: args.targetClassId,
          },
        });
        if (error) return { error };
        return { data: { id: data as string } };
      },
      invalidatesTags: (_result, _error, { sourceClassId }) => [
        { type: "ClassDistribution", id: sourceClassId },
      ],
    }),

    clearStudentDistributionTarget: builder.mutation<
      { removed: boolean },
      ClearStudentTargetArgs
    >({
      queryFn: async (args, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_distribution_clear_student_target",
          params: { p_student_id: args.studentId },
        });
        if (error) return { error };
        return { data: { removed: !!data } };
      },
      invalidatesTags: (_result, _error, { sourceClassId }) => [
        { type: "ClassDistribution", id: sourceClassId },
      ],
    }),

    /**
     * Active classes in the caller's school — for the Verteilungsziel dropdown.
     * RLS on structure_classes scopes to the user's school.
     */
    getDistributionEligibleClasses: builder.query<
      Array<{ id: string; name: string }>,
      void
    >({
      query: () => ({
        type: "select",
        table: "structure_classes",
        params: {
          select: "id,name",
          is_active: true,
          order: "name",
        },
      }),
      providesTags: [{ type: "ClassDistribution", id: "ELIGIBLE_CLASSES" }],
    }),
  }),
});

export const {
  useGetClassDistributionTargetsQuery,
  useSetStudentDistributionTargetMutation,
  useClearStudentDistributionTargetMutation,
  useGetDistributionEligibleClassesQuery,
} = classDistributionApi;
